import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Plus } from "styled-icons/feather";
import * as styles from "../styles";
import Button from "components/common/Button";
import ProfilesTab from "../components/ProfilesTab";
import PostsTab from "../components/PostsTab";
import ConfirmModal from "../components/ConfirmModal";
import { formatDateForDisplay, Tab } from "../utils";
import type { ProfileOption } from "../types";
import { setCfTab } from "../../../state/actions/ContentFactoryActions";
import { callApi } from "utils/ContentoApi";
import { bookADemoUrl } from "constants/booking";

type ResultPageProps = {
  accountId: string;
  initialTab: Tab;
  initialPendingBatchId?: string | null;
};

const ResultPage: React.FC<ResultPageProps> = ({
  accountId,
  initialTab,
  initialPendingBatchId,
}) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [atProfileLimit, setAtProfileLimit] = useState(false);
  const [expandProfileId, setExpandProfileId] = useState<string | null>(null);

  // Remember the active tab so returning to the content factory restores it.
  useEffect(() => {
    dispatch(setCfTab(activeTab));
  }, [activeTab, dispatch]);

  // "Go to profile" expands its target once; clear it on leaving so a later
  // return to the tab respects collapse-by-default instead of re-expanding.
  useEffect(() => {
    if (activeTab !== "profiles") setExpandProfileId(null);
  }, [activeTab]);

  const timezone =
    useSelector((state: { account: { data?: { timezone?: string } } }) => state.account.data?.timezone) ??
    "Europe/Brussels";

  const closeSourceModal = useCallback(() => setIsSourceModalOpen(false), []);

  const handleCreateProfileClick = useCallback(() => {
    if (atProfileLimit) {
      setIsLimitModalOpen(true);
      return;
    }
    setIsSourceModalOpen(true);
  }, [atProfileLimit]);

  const goToProfile = useCallback((profileId: string) => {
    setExpandProfileId(profileId);
    setActiveTab("profiles");
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await callApi({
        method: "get",
        url: `content-factory/accounts/${accountId}/profiles`,
      }, true, true);
      const rawProfiles = response?.profiles ?? [];
      setProfiles(rawProfiles.map((p: any) => ({
        id: p.id,
        name: p.name,
        createdAt: formatDateForDisplay(p.createdAt, timezone),
        customInstructions: p.customInstructions ?? null,
      })));
      setAtProfileLimit(response?.atProfileLimit ?? false);
    } catch {
      // Profile list is optional for the picker — fail silently
    }
  }, [accountId, timezone]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // A new profile stays a nameless placeholder until its generation job finishes,
  // and no create/delete handler sees that transition; refetch when the picker opens.
  useEffect(() => {
    if (isSourceModalOpen) fetchProfiles();
  }, [isSourceModalOpen, fetchProfiles]);

  return (
    <styles.FactoryPageContainer>
      <styles.FactoryContentWrapper>
        <styles.FactoryHeader>
          <styles.TabsContainer>
            <styles.TabButton
              isActive={activeTab === "profiles"}
              onClick={() => setActiveTab("profiles")}
            >
              Profiles
            </styles.TabButton>
            <styles.TabButton
              isActive={activeTab === "posts"}
              onClick={() => setActiveTab("posts")}
            >
              Posts
            </styles.TabButton>
          </styles.TabsContainer>

          {activeTab === "profiles" ? (
            <Button size="sm" onClick={handleCreateProfileClick}>
              <Plus size={17} strokeWidth={2} style={{ marginRight: 6 }} />
              Create new profile
            </Button>
          ) : (
            <Button size="sm" onClick={() => setIsSourceModalOpen(true)}>
              <Plus size={17} strokeWidth={2} style={{ marginRight: 6 }} />
              Generate new batch
            </Button>
          )}
        </styles.FactoryHeader>

        <div style={{ width: "100%" }}>
          {activeTab === "profiles" ? (
            <ProfilesTab
              accountId={accountId}
              isSourceModalOpen={isSourceModalOpen}
              onCloseSourceModal={closeSourceModal}
              expandProfileId={expandProfileId}
              onProfilesChanged={fetchProfiles}
            />
          ) : (
            <PostsTab
              accountId={accountId}
              profiles={profiles}
              isSourceModalOpen={isSourceModalOpen}
              onCloseSourceModal={closeSourceModal}
              onGoToProfile={goToProfile}
              initialPendingBatchId={initialPendingBatchId}
              onProfilesChanged={fetchProfiles}
            />
          )}
        </div>
      </styles.FactoryContentWrapper>

      <ConfirmModal
        modalName="profile-limit-modal"
        title="You've reached your profile limit"
        description="Your account has reached the maximum number of profiles. Upgrade to create more — book a demo and we'll find the right plan for you."
        confirmLabel="Upgrade to get more profiles"
        isOpen={isLimitModalOpen}
        onClose={() => setIsLimitModalOpen(false)}
        onConfirm={() => {
          window.open(bookADemoUrl("profile-limit"), "_blank");
          setIsLimitModalOpen(false);
        }}
      />
    </styles.FactoryPageContainer>
  );
};

export default ResultPage;
